/**
 * Right-side controls for V2AdminNavbar.
 *
 * Hiding the public marketing nav on /admin/* routes (the unification
 * change) removed the only path admins had to: switch language, jump
 * into the tenant Dashboard, view what tenants see on the public site,
 * and sign out. This component restores those affordances inside the
 * admin chrome itself, so the slim strip stays the only header but
 * still gives admins a way out / over.
 *
 * Mirrors the right-side block the public Navigation renders for
 * authenticated users, intentionally:
 *   - same Globe + EN/DE toggle
 *   - same "Dashboard" pill button as a dropdown trigger
 *   - same dropdown items (Dashboard / Public site / Sign out)
 *
 * Kept as its own component so V2AdminNavbar can stay focused on tab
 * routing logic, and so this menu can be re-used if we ever add a
 * second admin chrome (e.g. a mobile drawer).
 */
import { Link } from "react-router-dom";
import {
  Globe,
  Home as HomeIcon,
  LayoutDashboard,
  LogOut,
  Settings as SettingsIcon,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { MapPin, UserCog } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOwnerIdentity } from "@/hooks/useOwnerIdentity";
import type { AppRole } from "@/lib/roles";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import type { RouteItem } from "@/components/admin/v2/V2AdminNavbar";
import {
  isActive as isNavActive,
  hrefFor,
  STAFF_SETTINGS_NAV,
} from "@/components/admin/v2/V2AdminNavbar";

interface AdminUserMenuProps {
  ownerNavItems?: RouteItem[];
  labelFor?: (item: RouteItem) => string;
  pathname?: string;
  searchParams?: URLSearchParams;
}

export function AdminUserMenu({
  ownerNavItems,
  labelFor,
  pathname = "",
  searchParams = new URLSearchParams(),
}: AdminUserMenuProps = {}) {
  const { user, signOut, isOwnerPortalOnly, isDemoDualViewAccount, roles } =
    useAuth();
  const { language, setLanguage, t } = useLanguage();

  /**
   * Name and the invoice address, shared with the V3 sidebar's user block via
   * useOwnerIdentity so the two cannot print a different name for the same
   * account. See that hook for why only a resolved `lessor` address renders.
   */
  const { displayName, billingLines } = useOwnerIdentity(isOwnerPortalOnly);

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "de" : "en");
  };

  // Mirrors roleLabel()/roleBadgeClass() from AdminRolePanels.tsx. Kept as a
  // small local copy rather than a shared import so this display-only badge
  // has no dependency on the role-management panel's module.
  const ROLE_PRIORITY: AppRole[] = ["super_admin", "tech", "admin", "owner"];
  const primaryRole = ROLE_PRIORITY.find((r) => roles.includes(r));

  const roleLabel = (role: AppRole): string => {
    switch (role) {
      case "super_admin":
        return "Super Admin";
      case "tech":
        return "Tech";
      case "admin":
        return "Admin";
      case "owner":
        return "Owner";
      default:
        return role;
    }
  };

  const roleBadgeClass = (role: AppRole): string => {
    switch (role) {
      case "super_admin":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "tech":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "admin":
        return "bg-green-100 text-green-800 border-green-300";
      case "owner":
        return "bg-purple-100 text-purple-800 border-purple-300";
      default:
        return "bg-gray-100 text-gray-600 border-gray-300";
    }
  };

  // Defensive fallback that handles both t() return paths for missing
  // keys: production returns '', dev returns the literal key. `t(key)
  // || fallback` only handles the production case — in dev the key
  // string is truthy and "wins", so missing keys would surface as
  // "admin.userMenu.account" in the UI. The strict-equality guard
  // treats either failure mode as a miss.
  const tFallback = (key: string, fallback: string) => {
    const value = t(key);
    return value && value !== key ? value : fallback;
  };

  return (
    <div className="flex items-center gap-1 shrink-0 ml-2 pl-2 border-l border-border/60">
      <Button
        variant="outline"
        size="sm"
        onClick={toggleLanguage}
        className="h-8 px-2 flex items-center gap-1.5 text-xs font-semibold"
        title={`Switch to ${language === "en" ? "German" : "English"}`}
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="font-medium leading-none">{language.toUpperCase()}</span>
      </Button>

      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="relative h-8 px-3 flex items-center gap-1.5 text-xs max-w-[min(200px,42vw)] bg-primary text-primary-foreground hover:bg-primary/90"
              title={[displayName, user.email].filter(Boolean).join(" · ")}
            >
              <User className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline truncate font-medium">
                {displayName}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="font-normal space-y-0.5">
              <p className="text-sm font-medium truncate flex items-center gap-1.5">
                <span className="truncate">{displayName}</span>
                {primaryRole ? (
                  <Badge
                    variant="outline"
                    className={cn(
                      "px-1.5 py-0 text-[10px] font-semibold leading-4 shrink-0",
                      roleBadgeClass(primaryRole)
                    )}
                  >
                    {roleLabel(primaryRole)}
                  </Badge>
                ) : null}
              </p>
              {user.email ? (
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              ) : null}
              {billingLines.length > 0 ? (
                <div
                  className="flex items-start gap-1.5 pt-1 text-xs text-muted-foreground"
                  title={billingLines.join(", ")}
                >
                  <MapPin className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
                  <span className="min-w-0">
                    {billingLines.map((line) => (
                      <span key={line} className="block truncate">
                        {line}
                      </span>
                    ))}
                  </span>
                </div>
              ) : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {/* Owner portal nav items injected here */}
            {ownerNavItems && ownerNavItems.length > 0 && labelFor && (
              <>
                {ownerNavItems.map((item) => {
                  const Icon = item.icon;
                  const active = isNavActive(
                    item,
                    pathname,
                    searchParams,
                    "analytics"
                  );
                  const label = labelFor(item);
                  return (
                    <Link key={`${item.kind}:${item.slug}`} to={hrefFor(item)}>
                      <DropdownMenuItem
                        className={cn(
                          "cursor-pointer",
                          active && "bg-muted font-medium"
                        )}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {label}
                      </DropdownMenuItem>
                    </Link>
                  );
                })}
                <DropdownMenuSeparator />
              </>
            )}
            {/* Staff reach Settings from the strip now, so repeating it here
                is exactly the duplication that made Settings and Account &
                profile read as the same entry. Owners have no Settings on
                their strip by design, so theirs stays. */}
            {isOwnerPortalOnly ? (
              <>
                {/* COL-447: the demo account is also a tenant, so it needs a
                    way back out of the owner portal into its own tenant
                    dashboard. Every other owner has no tenant dashboard to
                    reach, so this entry stays hidden for them. */}
                {isDemoDualViewAccount ? (
                  <Link to="/dashboard">
                    <DropdownMenuItem className="cursor-pointer">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      {tFallback(
                        "admin.nav.tenantDashboard",
                        "Tenant dashboard"
                      )}
                    </DropdownMenuItem>
                  </Link>
                ) : null}
                <Link to={hrefFor(STAFF_SETTINGS_NAV)}>
                  <DropdownMenuItem
                    className={cn(
                      "cursor-pointer",
                      isNavActive(
                        STAFF_SETTINGS_NAV,
                        pathname,
                        searchParams,
                        "analytics"
                      ) && "bg-muted font-medium"
                    )}
                  >
                    <SettingsIcon className="w-4 h-4 mr-2" />
                    {tFallback("admin.nav.settings", STAFF_SETTINGS_NAV.label)}
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
              </>
            ) : null}
            {/* Staff only. The strip's Settings entry is org-level ("Roles &
                access"), so this is their way to their own name, email,
                documents, language and password — the same page owners get,
                minus Payouts. Owners reach theirs through Settings above, so
                offering both would put one page behind two entries a line
                apart. */}
            {!isOwnerPortalOnly && (
              <Link to="/admin/account">
                <DropdownMenuItem
                  className={cn(
                    "cursor-pointer",
                    pathname === "/admin/account" && "bg-muted font-medium"
                  )}
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  {tFallback(
                    "admin.userMenu.accountProfile",
                    "Account & profile"
                  )}
                </DropdownMenuItem>
              </Link>
            )}
            {!isOwnerPortalOnly && <DropdownMenuSeparator />}
            {/* Most-used first: a tenant Dashboard link is what admins
                lost when we hid the public nav, so it goes at the top.
                Both are staff shortcuts — an owner-portal account has no
                tenant dashboard to land on, so its menu stays Settings +
                Sign out, as the portal design shows. */}
            {!isOwnerPortalOnly && (
              <>
                <Link to="/dashboard">
                  <DropdownMenuItem className="cursor-pointer">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {tFallback("nav.dashboard", "Tenant dashboard")}
                  </DropdownMenuItem>
                </Link>
                <Link to="/">
                  <DropdownMenuItem className="cursor-pointer">
                    <HomeIcon className="w-4 h-4 mr-2" />
                    {tFallback("admin.userMenu.publicSite", "Public site")}
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              className="cursor-pointer text-destructive"
              onSelect={() => {
                void signOut();
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              {tFallback("auth.logout", "Sign out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
